import { create } from 'zustand';
import { STARTING, type Progress } from './run';

/**
 * A running import, held outside the component that started it.
 *
 * The add-item dialog kicks the pipeline off and then closes; the panel that
 * shows the progress mounts a moment later, somewhere else in the tree. Neither
 * can own this. Keyed by item id because the item exists before the import
 * does — that's what makes the panel openable straight away.
 */

export type Run = {
  url: string;
  progress: Progress;
  /** Set when the fetch failed. The panel offers to paste the page instead. */
  blocked: boolean;
  error: string | undefined;
  /** Offered by the page but unsaveable. Worth mentioning, never fatal. */
  failedImages: number;
  finished: boolean;
};

type ImportState = {
  /** Replaced rather than mutated on every change — Zustand compares by
      identity, and a mutated Map would never re-render. */
  runs: Map<string, Run>;

  start: (itemId: string, url: string) => void;
  advance: (itemId: string, progress: Progress) => void;
  fail: (itemId: string, error: string, blocked: boolean) => void;
  finish: (itemId: string, failedImages: number) => void;
  dismiss: (itemId: string) => void;
};

export const useImportStore = create<ImportState>((set, get) => ({
  runs: new Map(),

  start: (itemId, url) =>
    set({
      runs: withRun(get().runs, itemId, {
        url,
        progress: STARTING,
        blocked: false,
        error: undefined,
        failedImages: 0,
        finished: false,
      }),
    }),

  advance: (itemId, progress) => set({ runs: patch(get().runs, itemId, { progress }) }),

  fail: (itemId, error, blocked) =>
    set({ runs: patch(get().runs, itemId, { error, blocked, finished: true }) }),

  finish: (itemId, failedImages) =>
    set({ runs: patch(get().runs, itemId, { failedImages, finished: true }) }),

  dismiss: (itemId) => {
    const runs = new Map(get().runs);
    runs.delete(itemId);
    set({ runs });
  },
}));

/** The one run this panel cares about. Undefined is the normal case. */
export function useImportRun(itemId: string | undefined): Run | undefined {
  return useImportStore((state) => (itemId === undefined ? undefined : state.runs.get(itemId)));
}

function withRun(runs: Map<string, Run>, itemId: string, run: Run): Map<string, Run> {
  const next = new Map(runs);
  next.set(itemId, run);
  return next;
}

function patch(runs: Map<string, Run>, itemId: string, change: Partial<Run>): Map<string, Run> {
  const current = runs.get(itemId);
  // The item was deleted mid-import and its run dismissed with it.
  if (current === undefined) return runs;

  return withRun(runs, itemId, { ...current, ...change });
}
